import React, {useEffect, useState} from "react";
import {SubscriptionsOverview} from "../components/organization-unit-detail/subscription/SubscriptionsOverview";
import {OrganizationUnitHierarchyBreadcrumbs} from "../components/organization-unit-hierarchy-breadcrumb/OrganizationUnitHierarchyBreadcrumbs";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {makeStyles} from "@material-ui/core/styles";
import {WithOrganization} from "../components/organizations/organization-context";
import {ArrowBackIos} from "@material-ui/icons";
import {useQuery} from "../utils/function-util";
import Button from "@material-ui/core/Button";
import {OrganizationUnitMemberships} from "../components/organization-unit-detail/membership/OrganizationUnitMemberships";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import {Card, CardContent} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import Box from "@material-ui/core/Box";
import {OrganizationUnitMachines} from "../components/organization-unit-detail/machine/OrganizationUnitMachines";
import {useCapability} from "../auth/CapabilityContext";
import {Roles} from "../auth/roles";
import {SupportUnit} from "../components/support-unit/SupportUnit";
import {Domains} from "../models/capabilities";

const useStyles = makeStyles(theme => ({
    orgUnitDetail: {
        margin: theme.spacing(4),
        '& > *': {
            'margin-bottom': theme.spacing(4)
        }
    },
    top: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        alignItems: 'center'
    }
}));

export const OrganizationUnitDetail: React.FC<WithOrganization> = ({currentOrganization}) => {
    const classes = useStyles();
    const query = useQuery();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {organizationUnitId} = useParams<{ organizationUnitId: string }>();
    const {capabilities, hasRole} = useCapability();
    const defaultTab = query.get('tab') ?? '';
    const [tabIndex, setTabIndex] = useState(parseInt(defaultTab) || 0);
    const [showMachineTab, setShowMachineTab] = useState(false);
    const [showSupportUnitTab, setShowSupportUnitTab] = useState(false);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        const hasMachineCapability = capabilities
            && currentOrganization
            && capabilities[currentOrganization.id]
            && capabilities[currentOrganization.id][Domains.Machines];
        
        setShowMachineTab(hasMachineCapability || hasRole(Roles.ADMIN));
        setShowSupportUnitTab(hasRole(Roles.ADMIN));
    }, [capabilities, currentOrganization]);

    if (!organizationUnitId) {
        return <Navigate to='/organization-units' replace={false}/>;
    }

    const goBack = () => {
        navigate(`/organization-units/${organizationUnitId}`);
    };

    const handleChange = (event: any, newValue: any) => {
        setTabIndex(newValue);
    };

    const handleMembershipChanged = () => {
        setRefresh(r => r + 1);
    }

    const features = [
        {
            key: 'membership',
            header:  t('Subscription.Membership.Title'),
            panel:  <OrganizationUnitMemberships organizationId={currentOrganization.id}
                                                 organizationUnitId={organizationUnitId}
                                                 onMembershipChanged={handleMembershipChanged}/>,
            show: true,
        },
        {
            key: 'machines',
            header: t('Machine.Title'),
            panel: <OrganizationUnitMachines
                organizationId={currentOrganization.id}
                organizationUnitId={organizationUnitId}/>,
            show: showMachineTab
        },
        {
            key: 'support-units',
            header: t('SupportUnit.Title'),
            panel:  <SupportUnit
                organizationId={currentOrganization.id}
                organizationUnitId={parseInt(organizationUnitId)}/>,
            show: showSupportUnitTab
        }
    ]
        .filter(f => f.show)

    return (
        <div className={classes.orgUnitDetail}>
            <div className={classes.top}>
                <Button onClick={goBack}>
                    <ArrowBackIos color="primary"/>
                </Button>
                <OrganizationUnitHierarchyBreadcrumbs organizationId={currentOrganization.id}
                                                      organizationUnitId={organizationUnitId}/>
            </div>

            <SubscriptionsOverview organizationId={currentOrganization.id}
                                   organizationUnitId={organizationUnitId}
                                   refresh={refresh}/>
            <Card variant="outlined">
                <CardContent>
                    <Tabs value={tabIndex} onChange={handleChange} indicatorColor="primary" textColor="primary">
                        {
                            features.map(f => <Tab key={f.key} label={f.header} />)
                        }
                    </Tabs>
                    {
                        features.map((f,index) =>  <TabPanel key={f.key} index={index} value={tabIndex}>
                            {f.panel}
                        </TabPanel>)
                    }
                </CardContent>
            </Card>
        </div>
    );
};

function TabPanel({
                      index,
                      value,
                      children
                  }: any) {
    return (
        <Box
            p={3}
            hidden={value !== index}
            id={`organization-unit-detail-tab-${index}`}>
            {children}
        </Box>
    )
}
